import { Routes, Route, Navigate } from 'react-router-dom';
import { PublicLayout } from '@/layouts/PublicLayout';
import Home from '@/pages/Home';
import CoursesCatalogPage from '@/pages/courses';
import CourseDetail from '@/pages/courses/CourseDetail';
import SessionPlayer from '@/pages/courses/SessionPlayer';
import MinistriesIndexPage from '@/pages/ministries';
import MinistryDetailPage from '@/pages/ministries/[id]';
import KnowledgeBaseIndex from '@/pages/kb';
import KBArticle from '@/pages/kb/KBArticle';
import KBCategoryPage from '@/pages/kb/KBCategory';
import SearchPage from '@/pages/Search';
import LoginPage from '@/pages/auth/login';
import RegisterPage from '@/pages/auth/register';
import ForgotPasswordPage from '@/pages/auth/forgot-password';
import ResetPasswordPage from '@/pages/auth/reset-password';
import ConfirmEmailPage from '@/pages/auth/confirm-email';
import OnboardingPage from '@/pages/auth/onboarding';
import PlanningCentreCallbackPage from '@/pages/auth/planning-centre-callback';
import Account from '@/pages/Account';
import NotFound from '@/pages/NotFound';
import { RequireProfileComplete } from '@/components/auth/RequireProfileComplete';
import { AnonymousProgressMigrator } from '@/components/AnonymousProgressMigrator';

/**
 * Router for the public Resource Centre.
 *  - Anonymous browsing of the catalogue stays open.
 *  - Authenticated users are gated by RequireProfileComplete, which redirects
 *    them to /auth/onboarding until their member profile is filled.
 *  - Auth pages share the PublicLayout chrome so visitors can still use the
 *    header (Home / Courses / Knowledge Base / Search / theme toggle) while
 *    signing in or registering. The session player + KB article keep their
 *    own immersive chrome.
 */
export default function App() {
  return (
    <>
      <AnonymousProgressMigrator />
      <Routes>
        {/* Immersive player + KB article render their own chrome. */}
        <Route path="/courses/:slug/play/:sessionId" element={<SessionPlayer />} />

      {/* Everything else shares the PublicLayout header + footer. */}
      <Route
        path="*"
        element={
          <RequireProfileComplete>
            <PublicLayout>
              <Routes>
                {/* Public catalogue + KB. */}
                <Route path="/" element={<Home />} />
                <Route path="/courses" element={<CoursesCatalogPage />} />
                <Route path="/courses/:slug" element={<CourseDetail />} />
                <Route path="/ministries" element={<MinistriesIndexPage />} />
                <Route path="/ministries/:slug" element={<MinistryDetailPage />} />
                <Route path="/kb" element={<KnowledgeBaseIndex />} />
                <Route path="/kb/categories/:slug" element={<KBCategoryPage />} />
                <Route path="/kb/articles/:slug" element={<KBArticle />} />

                {/* Hard block on numeric-id deep links — they leak the
                    existence of rows that may be draft / archived / outside
                    the viewer's ministry. Any stale link of the form
                    /kb/articles/id/N or /courses/id/N silently redirects
                    to the public listing rather than producing a different
                    response for "exists" vs "doesn't exist". */}
                <Route path="/kb/articles/id" element={<Navigate to="/kb" replace />} />
                <Route path="/kb/articles/id/*" element={<Navigate to="/kb" replace />} />
                <Route path="/courses/id" element={<Navigate to="/courses" replace />} />
                <Route path="/courses/id/*" element={<Navigate to="/courses" replace />} />
                <Route path="/courses/sessions/*" element={<Navigate to="/courses" replace />} />
                <Route path="/search" element={<SearchPage />} />
                <Route path="/account" element={<Account />} />

                {/* Auth pages — header visible so visitors can navigate around. */}
                <Route path="/auth/login" element={<LoginPage />} />
                <Route path="/auth/register" element={<RegisterPage />} />
                <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
                <Route path="/auth/confirm-email" element={<ConfirmEmailPage />} />
                <Route path="/auth/onboarding" element={<OnboardingPage />} />
                <Route path="/auth/oauth/planning-centre/callback" element={<PlanningCentreCallbackPage />} />

                <Route path="*" element={<NotFound />} />
              </Routes>
            </PublicLayout>
          </RequireProfileComplete>
        }
      />
      </Routes>
    </>
  );
}
